/**
 * Signed-in UI prefs (locale + theme).
 *
 * Device cache: Paraglide localStorage + theme cookie for instant paint.
 * After auth, BE ``UserSettings`` is source of truth and overwrites the cache.
 * Switchers persist back to BE (fire-and-forget) so devices stay aligned.
 */
import { auth } from "./auth.svelte";
import { i18n } from "./locale.svelte";
import { theme, type ThemePreference } from "./theme.svelte";
import {
  IdentitySettingsDocument,
  IdentityUpdateSettingsDocument,
  type IdentitySettingsQuery,
  type IdentityUpdateSettingsMutation,
} from "$lib/graphql/generated/graphql";
import { mutationSuccess } from "$lib/graphql/payload";
import { locales, type Locale } from "$lib/paraglide/runtime";

const THEMES = new Set<ThemePreference>(["light", "dark", "system"]);

let loadedForUserId = $state<string | null>(null);
let loadPromise: Promise<void> | null = null;
let persistTimer: ReturnType<typeof setTimeout> | null = null;
let pendingPatch: { locale?: string; theme?: string } = {};

function isLocale(value: string): value is Locale {
  return (locales as readonly string[]).includes(value);
}

function parseLocale(raw: string | null | undefined): Locale | null {
  if (!raw) return null;
  const primary = raw.trim().toLowerCase().replace("_", "-").split("-", 1)[0] ?? "";
  return isLocale(primary) ? primary : null;
}

function parseTheme(raw: string | null | undefined): ThemePreference | null {
  if (!raw) return null;
  const value = raw.trim().toLowerCase();
  return THEMES.has(value as ThemePreference) ? (value as ThemePreference) : null;
}

function applyFromServer(row: IdentitySettingsQuery["identity"]["settings"]): void {
  const nextLocale = parseLocale(row.locale);
  if (nextLocale) i18n.setLocale(nextLocale);
  const nextTheme = parseTheme(row.theme);
  if (nextTheme) theme.set(nextTheme);
}

async function load(): Promise<void> {
  if (!auth.isAuthenticated) return;
  const userId = auth.user?.id ?? null;
  try {
    const data = await auth.client.request<IdentitySettingsQuery>(IdentitySettingsDocument, {});
    applyFromServer(data.identity.settings);
    loadedForUserId = userId;
  } catch {
    // Prefs must never break the app chrome.
  }
}

function schedulePersist(patch: { locale?: string; theme?: string }): void {
  if (!auth.isAuthenticated) return;
  pendingPatch = { ...pendingPatch, ...patch };
  if (persistTimer) clearTimeout(persistTimer);
  persistTimer = setTimeout(() => {
    void flushPersist();
  }, 250);
}

async function flushPersist(): Promise<void> {
  persistTimer = null;
  if (!auth.isAuthenticated) {
    pendingPatch = {};
    return;
  }
  const patch = pendingPatch;
  pendingPatch = {};
  if (!patch.locale && !patch.theme) return;
  try {
    const data = await auth.client.request<IdentityUpdateSettingsMutation>(
      IdentityUpdateSettingsDocument,
      {
        locale: patch.locale,
        theme: patch.theme,
      },
    );
    applyFromServer(mutationSuccess(data.identity.updateSettings).settings);
  } catch {
    // Local chrome already updated; retry on next change / reload.
  }
}

export const userPrefs = {
  /** Call once auth is resolved; no-op until signed in. */
  ensureLoaded(): void {
    if (!auth.isAuthenticated) {
      loadedForUserId = null;
      return;
    }
    if (loadedForUserId === (auth.user?.id ?? null)) return;
    if (!loadPromise) {
      loadPromise = load().finally(() => {
        loadPromise = null;
      });
    }
  },
  /** Force reload (e.g. after profile prefs save). */
  async reload(): Promise<void> {
    loadedForUserId = null;
    await load();
  },
  applyServerRow(row: IdentitySettingsQuery["identity"]["settings"]): void {
    applyFromServer(row);
    loadedForUserId = auth.user?.id ?? loadedForUserId;
  },
  setLocale(next: Locale, opts: { persist?: boolean } = {}): void {
    i18n.setLocale(next);
    if (opts.persist !== false) schedulePersist({ locale: next });
  },
  setTheme(next: ThemePreference, opts: { persist?: boolean } = {}): void {
    theme.set(next);
    if (opts.persist !== false) schedulePersist({ theme: next });
  },
  cycleTheme(opts: { persist?: boolean } = {}): void {
    const order: ThemePreference[] = ["light", "dark", "system"];
    const idx = order.indexOf(theme.preference);
    this.setTheme(order[(idx + 1) % order.length] ?? "system", opts);
  },
};
